import React, { useState, useContext } from 'react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { AuthContext } from '../../contexts/AuthContext'
import { handlePostRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import { useFileUpload } from '../../hooks/useFileUpload.jsx'
import { QueryKeys } from '../../utils/queryKeyGenerator.js'
import InputField from '../common/InputField.jsx'
import TextareaField from '../common/TextareaField.jsx'

const SupportModal = ({ open, onClose }) => {
  const { currentUser } = useContext(AuthContext)
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { renderUploadInput, handleFileUpload, isUploading, clearFiles } = useFileUpload({
    label: 'Attach File (optional)',
    dropZoneOptions: {
      required: false,
    },
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const attachmentUrl = await handleFileUpload()
      await handlePostRequest(`/${currentUser?.userType}s/support/tickets`, {
        subject,
        description,
        ...(attachmentUrl ? { attachmentUrl } : {}),
      })
      setSubject('')
      setDescription('')
      clearFiles()
      showToaster('success', 'Support ticket submitted successfully!')
      onClose()
      QueryKeys.supportTickets.invalidateAll()
    } catch (error) {
      showToaster('error', 'Failed to submit support ticket.')
    } finally {
      setIsLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="relative max-w-md w-full mx-auto p-8 bg-background rounded-lg shadow-lg flex flex-col items-center">
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
          onClick={onClose}
          aria-label="Close"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
        <div className="flex flex-col items-center mb-6">
          <h2 className="text-2xl font-bold text-center mb-2">Support Request Form</h2>
          <p className="text-gray-500 text-center text-base max-w-xs">
            Please describe your issue or question below. You can also attach a file (optional).
          </p>
        </div>
        <form className="w-full flex flex-col gap-6" onSubmit={handleSubmit}>
          <InputField
            id="ticketSubject"
            name="ticketSubject"
            type="text"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter the subject..."
            label="Subject"
          />

          <TextareaField
            id="ticketDescription"
            name="ticketDescription"
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your issue or question..."
            label="Ticket Description"
          />

          <div>{renderUploadInput()}</div>
          <button
            type="submit"
            className={`w-full py-3 mt-2 rounded-lg text-white text-lg font-medium bg-gradient-to-r from-orange-400 to-orange-600 shadow hover:opacity-90 transition ${
              isLoading || isUploading ? 'opacity-70 cursor-wait' : ''
            }`}
            disabled={isLoading || isUploading || !description || !subject}
          >
            {isLoading || isUploading ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default SupportModal
